/*
 * Storefront search: bypass semantic matching.
 *
 * Shopify's native search (Search & Discovery) runs a semantic layer that returns
 * products which don't contain the typed term, and it can't be turned off. Wrapping
 * the query in quotes puts it in "search syntax", which bypasses semantic matching
 * and does plain exact-word keyword search across the searchable fields.
 *
 * So "ohtani" is searched as "ohtani", and "ohtani rookie" as "ohtani" "rookie"
 * (both words must appear). The shopper never sees the quotes: the search box,
 * the no-results message and the page title are stripped back to the plain term.
 */
(function () {
  // Plain term -> quoted syntax. Each word is quoted so every word must match.
  function toQuoted(raw) {
    var words = String(raw == null ? '' : raw).trim().split(/\s+/);
    var quoted = [];
    for (var i = 0; i < words.length; i++) {
      var w = words[i].replace(/"/g, '').trim(); // drop stray quotes
      if (w) quoted.push('"' + w + '"');
    }
    return quoted.join(' ');
  }

  function isQuoted(q) {
    return typeof q === 'string' && q.indexOf('"') !== -1;
  }

  // Quoted syntax -> the plain term for display.
  function toPlain(q) {
    return String(q == null ? '' : q).replace(/"/g, '').replace(/\s+/g, ' ').trim();
  }

  // Keep the theme-preview context on programmatic navigations.
  function withPreview(url) {
    var previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewId) url.searchParams.set('preview_theme_id', previewId);
    return url;
  }

  // Rewrite a plain search to quoted syntax on submit.
  function handleSubmit(evt) {
    var form = evt.currentTarget;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var raw = input.value.trim();
    if (!raw || isQuoted(raw)) return; // empty, or the shopper used quotes themselves

    evt.preventDefault();
    var url = new URL(form.getAttribute('action'), window.location.origin);
    new URLSearchParams(new FormData(form)).forEach(function (value, key) {
      url.searchParams.set(key, value);
    });
    url.searchParams.set('q', toQuoted(raw));
    window.location.assign(withPreview(url).toString());
  }

  // Predictive suggestion links (and any link into /search) point at a plain query,
  // which would hit semantic search. Rewrite them to quoted syntax on click, keeping
  // the current host so a theme preview doesn't bounce to the live theme.
  function handleSearchLinkClick(evt) {
    if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
    var link = evt.target.closest('a[href]');
    if (!link) return;

    var url;
    try { url = new URL(link.href, window.location.origin); } catch (e) { return; }
    if (url.pathname.indexOf('/search') === -1) return;

    var query = url.searchParams.get('q');
    if (!query || isQuoted(query)) return; // no query, or already quoted

    url.searchParams.set('q', toQuoted(query));
    url.protocol = window.location.protocol;
    url.host = window.location.host;
    evt.preventDefault();
    window.location.assign(withPreview(url).toString());
  }

  // Strip the quotes from anything the shopper sees.
  function cleanupDisplay() {
    var q = new URLSearchParams(window.location.search).get('q');
    if (!isQuoted(q)) return;
    var clean = toPlain(q);

    document.querySelectorAll('input[name="q"]').forEach(function (input) {
      if (input.type !== 'hidden') input.value = clean;
    });
    document.querySelectorAll('[data-search-no-results]').forEach(function (el) {
      el.textContent = el.textContent.split(q).join(clean);
    });
    if (document.title.indexOf(q) !== -1) {
      document.title = document.title.split(q).join(clean);
    }
  }

  // Exact search has no typo tolerance. On zero results, fall back to Shopify's
  // predictive engine (typo-tolerant, NOT semantic) for a "Did you mean" correction
  // and the closest matches. Reuses the theme's existing predictive sections.
  function noResultsFallback() {
    var noResults = document.querySelector('[data-search-no-results]');
    if (!noResults || !window.theme || !theme.routes) return;
    var q = new URLSearchParams(window.location.search).get('q');
    if (!isQuoted(q)) return;

    var term = toPlain(q);
    var heading = noResults.closest('h5') || noResults;
    var container = heading.parentElement;
    var endpoint = theme.routes.predictiveSearch + '?q=' + encodeURIComponent(term);

    // "Did you mean ...?" from the existing search-suggestions section.
    fetch(endpoint + '&section_id=search-suggestions')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var item = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('.search-suggestions__list-item:not(.search-suggestions__list-item--collection) .search-suggestions__item');
        var text = item && item.textContent.trim();
        if (!text || text.toLowerCase() === term.toLowerCase()) return;

        var url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('q', toQuoted(text));

        var p = document.createElement('p');
        p.className = 'align-centre fully-spaced-row';
        var link = document.createElement('a');
        link.className = 'underline';
        link.href = withPreview(url).toString();
        link.textContent = text;
        p.appendChild(document.createTextNode('Did you mean '));
        p.appendChild(link);
        p.appendChild(document.createTextNode('?'));
        heading.insertAdjacentElement('afterend', p);
      });

    // Closest matches from the existing predictive-search section.
    fetch(endpoint + '&section_id=predictive-search&resources[limit]=8')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var grid = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-grid');
        if (!grid || !grid.querySelector('.product-block')) return;

        var label = document.createElement('p');
        label.className = 'align-centre fully-spaced-row';
        label.textContent = 'Closest matches';
        container.appendChild(label);
        container.appendChild(grid);
      });
  }

  function init() {
    document.querySelectorAll('form.main-search__form').forEach(function (form) {
      form.addEventListener('submit', handleSubmit);
    });
    document.addEventListener('click', handleSearchLinkClick);
    cleanupDisplay();
    noResultsFallback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
